if (!customElements.get('careers-application-form')) {
  const SUCCESS_MESSAGE = 'Thank you! Your application has been submitted.';
  const ERROR_MESSAGE = 'Something went wrong. Please try again.';
  const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5 MB

  class CareersApplicationForm extends HTMLElement {
    connectedCallback() {
      this.modal = this.querySelector('[data-careers-modal]');
      this.form = this.querySelector('[data-careers-form]');
      if (!this.modal || !this.form) return;

      this.submitBtn = this.querySelector('[data-careers-submit]');
      this.resumeInput = this.querySelector('input[type="file"]');
      this.toast = this.querySelector('[data-careers-toast]');

      this.googleScriptUrl = this.dataset.googleScriptUrl ?? '';
      this.sheetFrom = this.dataset.sheetFrom ?? 'careers';

      // Trigger buttons live elsewhere in the same section.
      const scope = this.closest('.shopify-section') || document;
      this.openHandler = this.open.bind(this);
      scope.querySelectorAll('[data-careers-apply-open]').forEach((btn) => {
        btn.addEventListener('click', this.openHandler);
      });

      this.querySelectorAll('[data-careers-close]').forEach((el) => {
        el.addEventListener('click', this.close.bind(this));
      });

      this.keydownHandler = (event) => {
        if (event.key === 'Escape') this.close();
      };

      this.form.addEventListener('submit', this.handleSubmit.bind(this));
    }

    open() {
      this.modal.hidden = false;
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this.keydownHandler);
      const firstField = this.form.querySelector('input, select');
      if (firstField) firstField.focus();
    }

    close() {
      this.modal.hidden = true;
      document.body.style.overflow = '';
      document.removeEventListener('keydown', this.keydownHandler);
    }

    async handleSubmit(event) {
      event.preventDefault();

      // Keep valid fields, clear only the invalid ones, then flag them.
      if (!this.form.checkValidity()) {
        this.clearInvalidFields();
        this.form.reportValidity();
        return;
      }

      const file = this.resumeInput.files[0];
      if (file && file.size > MAX_FILE_BYTES) {
        this.showToast('Resume must be 5 MB or smaller.', true);
        return;
      }

      this.setLoading(true);
      const ok = await this.submitToGoogleSheet(file);
      this.setLoading(false);

      if (ok) {
        this.rememberIdentity();
        this.showToast(SUCCESS_MESSAGE);
        this.form.reset();
        setTimeout(() => this.close(), 1500);
      } else {
        this.showToast(ERROR_MESSAGE, true);
      }
    }

    async submitToGoogleSheet(file) {
      if (!this.googleScriptUrl) return true;

      let resumeData = '';
      try {
        if (file) resumeData = await this.fileToBase64(file);
      } catch (error) {
        console.error('CareersApplication: could not read resume file', error);
        return false;
      }

      const payload = {
        formType: 'job-application',
        timestamp: new Date().toISOString(),
        name: this.getValue('name'),
        position: this.getValue('position'),
        email: this.getValue('email'),
        contact: this.getValue('contact'),
        resumeName: file ? file.name : '',
        resumeType: file ? file.type : '',
        resumeData,
        from: this.sheetFrom,
      };

      try {
        await fetch(this.googleScriptUrl, {
          method: 'POST',
          mode: 'no-cors',
          headers: { 'Content-Type': 'text/plain' },
          body: JSON.stringify(payload),
        });
        return true;
      } catch (error) {
        console.error('CareersApplication: Google Sheet submission failed', error);
        return false;
      }
    }

    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    }

    /**
     * Remembers the visitor so Browse Abandon has a ContactKey later.
     * Optional chaining keeps this a no-op if the helper is not loaded.
     */
    rememberIdentity() {
      window.ZdSfmcIdentity?.save({
        email: this.getValue('email'),
        name: this.getValue('name'),
        phone: this.getValue('contact'),
      });
    }

    clearInvalidFields() {
      this.form.querySelectorAll('input, select, textarea').forEach((field) => {
        if (!field.checkValidity()) field.value = '';
      });
    }

    getValue(name) {
      const el = this.form.querySelector(`[name="${name}"]`);
      return el ? el.value.trim() : '';
    }

    setLoading(isLoading) {
      if (!this.submitBtn) return;
      if (isLoading) {
        this.submitBtn.setAttribute('disabled', '');
        this.submitBtn.textContent =
          this.submitBtn.getAttribute('data-loading-text') || 'Submitting...';
      } else {
        this.submitBtn.removeAttribute('disabled');
        this.submitBtn.textContent =
          this.submitBtn.getAttribute('data-default-text') || 'Submit Application';
      }
    }

    showToast(message, isError = false) {
      if (!this.toast) return;

      this.toast.textContent = message;
      this.toast.classList.toggle('careers-application__toast--error', isError);
      this.toast.classList.add('careers-application__toast--visible');

      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => {
        this.toast.classList.remove('careers-application__toast--visible');
      }, 4000);
    }

    disconnectedCallback() {
      clearTimeout(this.toastTimer);
      document.removeEventListener('keydown', this.keydownHandler);
      document.body.style.overflow = '';
    }
  }

  customElements.define('careers-application-form', CareersApplicationForm);
}
